/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "./css/FareConfigure.css";
import "./css/FareConfigureList.css";
import {
  saveFareConfig,
  listFareConfigs,
  deleteFareConfig,
} from "../api/fareConfigureApi";
import { toast } from "react-toastify";
import AccessControl from "../components/AccessControl";
import Loader from "../components/Loader";

/* ─── Zones fetch (same as before) ───────────────────────────────── */
const ZONE_API = axios.create({
  baseURL: `${process.env.REACT_APP_SOCKET_URL}/api`,
});
const authHdrs = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

async function fetchZones() {
  try {
    const adminData = localStorage.getItem("admin");
    const userData = adminData ? JSON.parse(adminData) : null;
    const orgId = userData?.orgId || "";
    if (!orgId) return [];
    const res = await ZONE_API.get(`/zones/${orgId}/zones`, authHdrs());
    return (res.data?.zones || []).filter((z) => z.isActive !== false);
  } catch {
    return [];
  }
}

/* ─── Constants / helpers ────────────────────────────────────────── */
const TYPE_ORDER = { NATIONAL: 1, REGIONAL: 2, AREA: 3, TERRITORY: 4 };

const DEFAULT_NIGHT = {
  enabled: false,
  startHour: 22,
  endHour: 6,
  surgeType: "flat",
  surgeValue: 30,
};

const BLANK_DRAFT = {
  commission: 10,
  minFare: 40,
  baseKm: 1,
  perKm: 12,
  surgeEnabled: true,
  operatingRangeKm: 25,
  maxRideDistance: 200,
  nightSurge: DEFAULT_NIGHT,
};

const GLOBAL_ROW = {
  zoneId: "",
  name: "Global Default",
  type: "—",
  isDefault: true,
};

const fmtHour = (h) => `${String(h).padStart(2, "0")}:00`;

const toDraft = (cfg) => ({
  commission: cfg.commission ?? "",
  minFare: cfg.minFare ?? "",
  baseKm: cfg.baseKm ?? 1,
  perKm: cfg.perKm ?? "",
  surgeEnabled: cfg.surgeEnabled ?? true,
  operatingRangeKm: cfg.operatingRangeKm ?? "",
  maxRideDistance: cfg.maxRideDistance ?? "",
  nightSurge: { ...DEFAULT_NIGHT, ...(cfg.nightSurge || {}) },
});

const Toggle = ({ value, onChange }) => (
  <div
    className={`toggle ${value ? "on" : "off"}`}
    onClick={() => onChange(!value)}
  >
    <div className="toggle-knob" />
  </div>
);

/* ═══════════════════════════════════════════════════════════════════ */

export default function FareConfigure() {
  const [zones, setZones] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // list controls
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | configured | pending

  // editor modal
  const [editing, setEditing] = useState(null); // row object | null
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [prefillFrom, setPrefillFrom] = useState(null); // name of zone we copied values from

  /* ─── Load ─────────────────────────────────────────────────────── */
  useEffect(() => {
    async function init() {
      const [zoneList, configRes] = await Promise.all([
        fetchZones(),
        listFareConfigs(),
      ]);
      setZones(zoneList);
      setConfigs(configRes?.configs || []);
      setLoading(false);
    }
    init();
  }, []);

  // Close modal on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && setEditing(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving]);

  /* ─── Derived data ─────────────────────────────────────────────── */
  const configByZone = useMemo(() => {
    const map = {};
    configs.forEach((c) => {
      map[c.isDefault ? "" : c.zoneId] = c;
    });
    return map;
  }, [configs]);

  const zonesById = useMemo(() => {
    const map = {};
    zones.forEach((z) => (map[z.zoneId] = z));
    return map;
  }, [zones]);

  const rows = useMemo(() => {
    const sorted = [...zones].sort(
      (a, b) =>
        (TYPE_ORDER[a.type] || 9) - (TYPE_ORDER[b.type] || 9) ||
        a.name.localeCompare(b.name),
    );
    return [GLOBAL_ROW, ...sorted];
  }, [zones]);

  // Walk parent chain → which config does this zone currently inherit?
  const findInherited = (row) => {
    let id = row.parentZoneId;
    while (id) {
      if (configByZone[id]) {
        return { name: zonesById[id]?.name || id, config: configByZone[id] };
      }
      id = zonesById[id]?.parentZoneId;
    }
    if (configByZone[""]) {
      return { name: "Global Default", config: configByZone[""] };
    }
    return null;
  };

  const visibleRows = rows.filter((r) => {
    const has = !!configByZone[r.zoneId];
    if (filter === "configured" && !has) return false;
    if (filter === "pending" && has) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const configuredCount = rows.filter((r) => configByZone[r.zoneId]).length;

  /* ─── Actions ──────────────────────────────────────────────────── */
  const openEditor = (row) => {
    const own = configByZone[row.zoneId];
    if (own) {
      setDraft(toDraft(own));
      setPrefillFrom(null);
    } else {
      // No override yet → start from the inherited values so admin only tweaks
      const inh = row.isDefault ? null : findInherited(row);
      setDraft(inh ? toDraft(inh.config) : BLANK_DRAFT);
      setPrefillFrom(inh ? inh.name : null);
    }
    setEditing(row);
  };

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setNS = (key, value) =>
    setDraft((d) => ({ ...d, nightSurge: { ...d.nightSurge, [key]: value } }));

  const handleSave = async () => {
    const required = [
      "commission",
      "minFare",
      "baseKm",
      "perKm",
      "operatingRangeKm",
      "maxRideDistance",
    ];
    if (required.some((k) => draft[k] === "" || Number(draft[k]) < 0)) {
      return toast.error("Please fill all fare fields with valid numbers");
    }
    const ns = draft.nightSurge;
    if (ns.enabled) {
      const bad = (h) => h === "" || h < 0 || h > 23;
      if (bad(ns.startHour) || bad(ns.endHour))
        return toast.error("Night surge hours must be between 0 and 23");
    }

    setSaving(true);
    const res = await saveFareConfig({
      zoneId: editing.zoneId || undefined,
      commission: Number(draft.commission),
      minFare: Number(draft.minFare),
      baseKm: Number(draft.baseKm),
      perKm: Number(draft.perKm),
      surgeEnabled: draft.surgeEnabled,
      operatingRangeKm: Number(draft.operatingRangeKm),
      maxRideDistance: Number(draft.maxRideDistance),
      nightSurge: {
        ...ns,
        startHour: Number(ns.startHour),
        endHour: Number(ns.endHour),
        surgeValue: Number(ns.surgeValue),
      },
    });
    setSaving(false);

    if (res.success) {
      toast.success(`${editing.name} fare saved`);
      // update list in place — no refetch needed
      setConfigs((prev) => {
        const key = editing.zoneId;
        const rest = prev.filter(
          (c) => (c.isDefault ? "" : c.zoneId) !== key,
        );
        return [...rest, res.config];
      });
      setEditing(null);
    } else {
      toast.error("Error: " + (res.error || res.message));
    }
  };

  const handleReset = async (row) => {
    if (
      !window.confirm(
        `Remove custom fare for "${row.name}"? It will use the parent/default fare again.`,
      )
    )
      return;
    setSaving(true);
    const res = await deleteFareConfig(row.zoneId);
    setSaving(false);
    if (res.success) {
      toast.success("Custom fare removed");
      setConfigs((prev) => prev.filter((c) => c.zoneId !== row.zoneId));
      setEditing(null);
    } else {
      toast.error("Error: " + (res.error || res.message));
    }
  };

  /* ─── Render ───────────────────────────────────────────────────── */
  return (
    <div className="fare-config">
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />
      {(loading || saving) && <Loader />}

      {/* ── Header + toolbar ── */}
      <div className="card section">
        <div className="fcl-head">
          <div>
            <h2 className="fcl-title">Fare Configurations</h2>
            <p className="fcl-sub">
              {configuredCount} of {rows.length} zones have fares set. Zones
              without their own fare use the parent zone / Global Default.
            </p>
          </div>
        </div>

        <div className="fcl-toolbar">
          <input
            className="fcl-search"
            placeholder="🔍 Search zone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="fcl-chips">
            {[
              ["all", "All"],
              ["configured", "Configured"],
              ["pending", "Not configured"],
            ].map(([key, label]) => (
              <button
                key={key}
                className={`fcl-chip ${filter === key ? "active" : ""}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── All zones list ── */}
        <div className="fcl-table-wrap">
          <table className="fcl-table">
            <thead>
              <tr>
                <th>Zone</th>
                <th>Type</th>
                <th>Min Fare</th>
                <th>Per KM</th>
                <th>Service Charge</th>
                <th>Range</th>
                <th>Max Ride</th>
                <th>Night Surge</th>
                <th>Status</th>
                <th className="fcl-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const cfg = configByZone[r.zoneId];
                const inh = !cfg && !r.isDefault ? findInherited(r) : null;
                return (
                  <tr key={r.zoneId || "default"}>
                    <td className="fcl-zone">{r.name}</td>
                    <td>
                      <span className="fcl-type">{r.type}</span>
                    </td>
                    {cfg ? (
                      <>
                        <td>
                          ₹{cfg.minFare}
                          <small style={{ color: "#9ca3af" }}>
                            {" "}
                            / {cfg.baseKm ?? 1} km
                          </small>
                        </td>
                        <td>₹{cfg.perKm}/km</td>
                        <td>₹{cfg.commission}</td>
                        <td>{cfg.operatingRangeKm} km</td>
                        <td>{cfg.maxRideDistance} km</td>
                        <td>
                          {cfg.nightSurge?.enabled
                            ? `${fmtHour(cfg.nightSurge.startHour)}–${fmtHour(
                                cfg.nightSurge.endHour,
                              )} · ${
                                cfg.nightSurge.surgeType === "flat"
                                  ? `+₹${cfg.nightSurge.surgeValue}`
                                  : `+${cfg.nightSurge.surgeValue}%`
                              }`
                            : "Off"}
                        </td>
                        <td>
                          <span className="status-badge configured">
                            ✅ Configured
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td colSpan={6} className="fcl-inherit">
                          {inh
                            ? `Using ${inh.name} fare (₹${inh.config.minFare} min · ₹${inh.config.perKm}/km)`
                            : "No fare set yet"}
                        </td>
                        <td>
                          <span className="status-badge not-configured">
                            ○ Not configured
                          </span>
                        </td>
                      </>
                    )}
                    <td className="fcl-right">
                      <button
                        className={`fcl-btn ${cfg ? "edit" : "setup"}`}
                        onClick={() => openEditor(r)}
                      >
                        {cfg ? "✏️ Update" : "＋ Set fare"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="fcl-empty">
                    No zones match your search / filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Edit modal ── */}
      {editing && (
        <div
          className="fcl-overlay"
          onMouseDown={(e) =>
            e.target === e.currentTarget && !saving && setEditing(null)
          }
        >
          <div className="fcl-modal">
            <div className="fcl-modal-head">
              <div>
                <h3>{editing.name}</h3>
                <span className="fcl-type">{editing.type}</span>
              </div>
              <button
                className="fcl-close"
                onClick={() => setEditing(null)}
                disabled={saving}
              >
                ✕
              </button>
            </div>

            <div className="fcl-modal-body">
              {prefillFrom && (
                <div className="fcl-note">
                  Values pre-filled from <b>{prefillFrom}</b>. Change what you
                  need and save to create a custom fare for this zone.
                </div>
              )}

              <div className="fcl-section-title">Fare</div>

              <NumberRow
                label="Service Charge"
                sub="Amount deducted from each ride"
                prefix="₹"
                value={draft.commission}
                onChange={(v) => setField("commission", v)}
              />
              <NumberRow
                label="Minimum Fare"
                sub="Fare for the first km (see Base Distance)"
                prefix="₹"
                value={draft.minFare}
                onChange={(v) => setField("minFare", v)}
              />
              <NumberRow
                label="Base Distance"
                sub="Km covered by the Minimum Fare"
                suffix="km"
                value={draft.baseKm}
                onChange={(v) => setField("baseKm", v)}
                min={0}
              />
              <NumberRow
                label="Per KM Rate"
                sub="Added for every extra km after Base Distance"
                prefix="₹"
                suffix="/km"
                value={draft.perKm}
                onChange={(v) => setField("perKm", v)}
              />
              <div className="fcl-note" style={{ background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" }}>
                Example: {draft.baseKm || 0} km → ₹{Number(draft.minFare) || 0} ·{" "}
                {(Number(draft.baseKm) || 0) + 1} km → ₹
                {(Number(draft.minFare) || 0) + (Number(draft.perKm) || 0)} ·{" "}
                {(Number(draft.baseKm) || 0) + 2} km → ₹
                {(Number(draft.minFare) || 0) + 2 * (Number(draft.perKm) || 0)}
              </div>
              <NumberRow
                label="Operating Range"
                sub="Allowed operating radius"
                suffix="km"
                value={draft.operatingRangeKm}
                onChange={(v) => setField("operatingRangeKm", v)}
              />
              <NumberRow
                label="Max Ride Distance"
                sub="Maximum ride distance allowed"
                suffix="km"
                value={draft.maxRideDistance}
                onChange={(v) => setField("maxRideDistance", v)}
              />
              <div className="setting-row">
                <div>
                  <div className="setting-label">Surge Pricing</div>
                  <div className="setting-sub">
                    Enable dynamic pricing during peak hours
                  </div>
                </div>
                <Toggle
                  value={draft.surgeEnabled}
                  onChange={(v) => setField("surgeEnabled", v)}
                />
              </div>

              <div className="fcl-section-title">🌙 Night Surge</div>

              <div className="setting-row">
                <div>
                  <div className="setting-label">Night Surge</div>
                  <div className="setting-sub">
                    Extra charge during late-night hours
                  </div>
                </div>
                <Toggle
                  value={draft.nightSurge.enabled}
                  onChange={(v) => setNS("enabled", v)}
                />
              </div>

              {draft.nightSurge.enabled && (
                <>
                  <NumberRow
                    label="Start Hour"
                    sub={`Begins at ${fmtHour(draft.nightSurge.startHour || 0)} (IST)`}
                    suffix="hr"
                    value={draft.nightSurge.startHour}
                    onChange={(v) => setNS("startHour", v)}
                    min={0}
                    max={23}
                  />
                  <NumberRow
                    label="End Hour"
                    sub={`Ends at ${fmtHour(draft.nightSurge.endHour || 0)} (IST)`}
                    suffix="hr"
                    value={draft.nightSurge.endHour}
                    onChange={(v) => setNS("endHour", v)}
                    min={0}
                    max={23}
                  />
                  <div className="setting-row">
                    <div>
                      <div className="setting-label">Surge Type</div>
                      <div className="setting-sub">
                        Flat ₹ or % on base fare
                      </div>
                    </div>
                    <select
                      value={draft.nightSurge.surgeType}
                      onChange={(e) => setNS("surgeType", e.target.value)}
                      className="input-sm select-input"
                    >
                      <option value="flat">Flat (₹)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                  <NumberRow
                    label="Surge Value"
                    sub={
                      draft.nightSurge.surgeType === "flat"
                        ? "Extra ₹ added to every night ride"
                        : "% added on top of base fare"
                    }
                    prefix={draft.nightSurge.surgeType === "flat" ? "₹" : null}
                    suffix={
                      draft.nightSurge.surgeType === "percentage" ? "%" : null
                    }
                    value={draft.nightSurge.surgeValue}
                    onChange={(v) => setNS("surgeValue", v)}
                    min={0}
                  />
                  <div className="night-surge-preview">
                    <span className="preview-icon">🌙</span>
                    <span className="preview-text">
                      {fmtHour(draft.nightSurge.startHour || 0)} –{" "}
                      {fmtHour(draft.nightSurge.endHour || 0)} &nbsp;|&nbsp;{" "}
                      {draft.nightSurge.surgeType === "flat"
                        ? `+₹${draft.nightSurge.surgeValue || 0} flat`
                        : `+${draft.nightSurge.surgeValue || 0}% on base fare`}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="fcl-modal-foot">
              {!editing.isDefault && configByZone[editing.zoneId] ? (
                <button
                  className="fcl-btn danger"
                  onClick={() => handleReset(editing)}
                  disabled={saving}
                >
                  🗑️ Remove custom fare
                </button>
              ) : (
                <span />
              )}
              <div className="fcl-foot-right">
                <button
                  className="fcl-btn ghost"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Fare"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Small reusable number row (defined OUTSIDE the component so inputs
       don't lose focus on every keystroke) ──────────────────────────── */
function NumberRow({ label, sub, prefix, suffix, value, onChange, min, max }) {
  return (
    <div className="setting-row">
      <div>
        <div className="setting-label">{label}</div>
        {sub && <div className="setting-sub">{sub}</div>}
      </div>
      <div className="input-group">
        {prefix && <span className="input-prefix">{prefix}</span>}
        <input
          type="number"
          min={min}
          max={max}
          value={value ?? ""}
          onChange={(e) =>
            onChange(e.target.value === "" ? "" : Number(e.target.value))
          }
          className="input-sm"
        />
        {suffix && <span className="input-suffix">{suffix}</span>}
      </div>
    </div>
  );
}